import { MESSAGES } from "@/constants/messages";
import {
  DOB_SLASH_FORMAT,
  DOB_CAPTURE,
  DOB_THEN_LASTNAME,
  LASTNAME_THEN_DOB,
  LASTNAME_ONLY,
  LASTNAME_WITH_NUMBERS_A,
  LASTNAME_WITH_NUMBERS_B,
  ALLOWED_DOB_LASTNAME_CHARS,
  MULTIPLE_SPACES,
  ANY_WHITESPACE,
  ALPHANUMERIC,
} from "@/constants/regex";

export function formatDisplayName(
  firstName?: string,
  lastName?: string
): string {
  const safeFirst = (firstName || "").trim();
  const safeLast = (lastName || "").trim();

  if (safeFirst && safeLast) {
    return `${safeFirst} ${safeLast.charAt(0).toUpperCase()}.`;
  }

  if (safeFirst) {
    return safeFirst;
  }

  if (safeLast) {
    return `${safeLast.charAt(0).toUpperCase()}.`;
  }

  return "";
}

export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

export function validateDOBLastName(input: string): ValidationResult { // NOSONAR
  const trimmed = input.trim();

  // Check if user entered name with trailing space but no date
  if (input !== trimmed && /^[A-Za-z]+\s+$/.test(input)) {
    return { isValid: false, errorMessage: MESSAGES.enterCompleteDob };
  }

  // Check if user entered date with trailing space but no lastname
  if (input !== trimmed && /^\d{1,2}\/\d{1,2}\/\d{4}\s+$/.test(input)) {
    return { isValid: false, errorMessage: MESSAGES.first3LettersAfterDob };
  }

  if (MULTIPLE_SPACES.test(trimmed)) {
    return {
      isValid: false,
      errorMessage: MESSAGES.onlyOneSpaceBetweenDobAndLastName,
    };
  }

  if (!trimmed) {
    return { isValid: false, errorMessage: MESSAGES.enterCompleteDob };
  }

  const dobLastNameMatch = trimmed.match(DOB_THEN_LASTNAME);
  if (dobLastNameMatch) {
    const [, dob, lastName] = dobLastNameMatch;

    const dobValidation = isValidDOB(dob);
    if (dobValidation !== true) {
      return { isValid: false, errorMessage: dobValidation };
    }

    if (lastName.length < 3) {
      return { isValid: false, errorMessage: MESSAGES.lastNameMin3 };
    }

    return { isValid: true };
  }

  const lastNameDOBMatch = trimmed.match(LASTNAME_THEN_DOB);
  if (lastNameDOBMatch) {
    const [, lastName, dob] = lastNameDOBMatch;

    if (lastName.length < 3) {
      return { isValid: false, errorMessage: MESSAGES.lastNameMin3 };
    }

    const dobValidation = isValidDOB(dob);
    if (dobValidation !== true) {
      return { isValid: false, errorMessage: dobValidation };
    }

    return { isValid: true };
  }

  // Check if last name contains special characters (not letters)
  // This checks both "lastname DOB" and "DOB lastname" formats
  // For "lastname DOB" format: check special chars before the date
  const lastnameBeforeDate =
    /^([A-Za-z]*[^A-Za-z0-9\/\s]+[A-Za-z]*)\s+\d{1,2}\/\d{1,2}\/\d{4}$/.test(
      trimmed
    );
  // For "DOB lastname" format: check special chars after the date
  const lastnameAfterDate =
    /^\d{1,2}\/\d{1,2}\/\d{4}\s+([A-Za-z]*[^A-Za-z\/\s]+[A-Za-z]*)$/.test(
      trimmed
    );

  if (lastnameBeforeDate || lastnameAfterDate) {
    return { isValid: false, errorMessage: MESSAGES.lastNameLettersOnly };
  }

  // Check if lastname contains numbers (like dun2 or 2dun)
  if (
    LASTNAME_WITH_NUMBERS_A.test(trimmed) ||
    LASTNAME_WITH_NUMBERS_B.test(trimmed)
  ) {
    return { isValid: false, errorMessage: MESSAGES.lastNameLettersOnly };
  }

  // Check if input is just a valid DOB without lastname
  if (DOB_SLASH_FORMAT.test(trimmed)) {
    return { isValid: false, errorMessage: MESSAGES.first3LettersAfterDob };
  }

  // Check if input is just a lastname without DOB
  if (LASTNAME_ONLY.test(trimmed)) {
    return { isValid: false, errorMessage: MESSAGES.enterCompleteDob };
  }

  // Check if there's a date-like pattern (numbers with slashes) in wrong format
  const hasDateLikePattern = /\d{1,4}\/\d{1,2}\/\d{1,4}/.test(trimmed);
  if (hasDateLikePattern) {
    return { isValid: false, errorMessage: MESSAGES.dobFormat };
  }

  const partialMatch = trimmed.match(
    new RegExp(`${DOB_SLASH_FORMAT.source}\\s+([A-Za-z]{1,2})$`)
  );
  if (partialMatch) {
    return { isValid: false, errorMessage: MESSAGES.first3LettersAfterDob };
  }

  if (!ALLOWED_DOB_LASTNAME_CHARS.test(trimmed)) {
    return { isValid: false, errorMessage: MESSAGES.onlyNumbersDobLettersLast };
  }

  return { isValid: false, errorMessage: MESSAGES.enterCompleteDob };
}

export function validateMedicaidID(input: string): ValidationResult {
  const trimmed = input.trim();

  // Disallow any internal spaces for a single ID
  if (ANY_WHITESPACE.test(trimmed)) {
    return { isValid: false, errorMessage: MESSAGES.medicaidNoSpaces };
  }

  // Check if input is empty
  if (!trimmed) {
    return { isValid: false, errorMessage: MESSAGES.medicaidEnterValid };
  }

  // Check for invalid characters (only alphanumeric allowed)
  if (!ALPHANUMERIC.test(trimmed)) {
    return { isValid: false, errorMessage: MESSAGES.medicaidAlphanumeric };
  }

  // Check for valid length (assuming 9-12 characters is valid)
  if (trimmed.length < 9 || trimmed.length > 12) {
    return { isValid: false, errorMessage: MESSAGES.medicaidEnterValid };
  }

  return { isValid: true };
}

function isValidDOB(dob: string): true | string {
  const match = dob.match(DOB_CAPTURE);

  if (!match) return MESSAGES.dobFormat;

  const [, month, day, year] = match;
  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);
  const yearNum = parseInt(year, 10);

  if (monthNum < 1 || monthNum > 12) return MESSAGES.dobFormat;
  if (dayNum < 1 || dayNum > 31) return MESSAGES.dobFormat;
  if (yearNum < 1900) return MESSAGES.dobFormat;
  const date = new Date(yearNum, monthNum - 1, dayNum);

  // Check if the date is valid (e.g., not 02/31/2020)
  if (
    date.getFullYear() !== yearNum ||
    date.getMonth() !== monthNum - 1 ||
    date.getDate() !== dayNum
  ) {
    return MESSAGES.dobFormat;
  }

  // Check for future date
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

  if (date > today) {
    return MESSAGES.futureDate;
  }

  return true;
}

// Calculates age in full years from a DOB string (ISO or MM/DD/YYYY).
// Returns 0 if the date is invalid or in the future.
export function calculateAgeFromDob(dob: string): number {
  if (!dob) return 0;
  const parsed = new Date(dob);
  if (isNaN(parsed.getTime())) {
    // Try parsing MM/DD/YYYY
    const parts = dob.split("/");
    if (parts.length === 3) {
      const [mm, dd, yyyy] = parts.map((p) => parseInt(p, 10));
      const fallback = new Date(yyyy, (mm || 1) - 1, dd || 1);
      if (!isNaN(fallback.getTime())) {
        return computeAge(fallback);
      }
    }
    return 0;
  }
  return computeAge(parsed);
}

function computeAge(date: Date): number {
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const m = today.getMonth() - date.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < date.getDate())) {
    age--;
  }
  return age < 0 ? 0 : age;
}

// Attempts to derive a city name from a single-line street address string.
// If a reliable city cannot be derived, returns the provided fallbackCity (if any) or an empty string.
export function extractCityFromAddress(
  address: string,
  fallbackCity?: string
): string {
  const safe = (address || "").trim();
  if (!safe) return fallbackCity || "";

  // Common pattern: "street, area, City[, State][, Zip]"
  const parts = safe
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length >= 2) {
    // Try last non-numeric segment as city, but skip 2-letter state codes
    for (let i = parts.length - 1; i >= 0; i--) {
      const segment = parts[i];
      const hasDigit = /\d/.test(segment);
      const isStateCode = /^[A-Z]{2}$/.test(segment);
      if (!hasDigit && segment.length > 1 && !isStateCode) {
        return segment;
      }
    }
  }
  return fallbackCity || "";
}

// Returns up to two-letter initials from a full name string. Skips common titles (e.g., Dr., Mr, Ms).
export function getInitialsFromName(name?: string): string {
  const raw = (name || "").trim();
  if (!raw) return "";
  const withoutTitle = raw
    .replace(/^dr\.?\s+/i, "")
    .replace(/^mr\.?\s+/i, "")
    .replace(/^mrs\.?\s+/i, "")
    .replace(/^ms\.?\s+/i, "")
    .trim();
  const parts = withoutTitle.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  const first = parts[0].charAt(0);
  const second = parts.length > 1 ? parts[1].charAt(0) : "";
  return (first + second).toUpperCase();
}

/**
 * Formats a date string without timezone conversion.
 * For ISO strings like "1981-07-19T00:00:00.000Z", extracts date parts directly
 * to avoid timezone offset issues. Falls back to standard Date parsing for other formats.
 */
export const formatDateWithoutTimezone = (dateString: string): string => {
  if (!dateString || dateString === "-") return "-";

  // YYYY-MM-DD (Care Management API)
  const dateOnly = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    return `${m}/${d}/${y}`;
  }

  // ISO with time (Demographics)
  const iso = dateString.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (iso) {
    const [, y, m, d] = iso;
    return `${m}/${d}/${y}`;
  }

  return dateString;
};

export const formatDate = (dateString: string) => {
  return formatDateWithoutTimezone(dateString);
};

export const formatPhoneNumber = (phone: string): string => {
  if (!phone || typeof phone !== "string" || phone.trim() === "-") return "-";

  const digits = phone.replace(/\D/g, "").trim();
  if (digits.length !== 10) return "-";

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// All available dashboard card types
export const ALL_CARD_TYPES = [
  "DEMOGRAPHICS",
  "PCP",
  "HEALTH_PLAN",
  "CARE_MANAGEMENT",
  "ADT_ALERTS",
  "RISK_STRATIFICATION",
  "CARE_GAPS",
  "IMMUNIZATIONS",
  "BEHAVIORAL_HEALTH_DIAGNOSES",
  "MEDICAL_DIAGNOSES",
  "RECENT_VISITS_ENCOUNTERS",
  "TWELVE_MONTH_COST_UTILIZATION",
  "MEDICATION_FILL_HISTORY",
  "SDOH_BARRIERS",
  "REFERRALS",
] as const;
